/**
 * DomainRegistry — the data that drives the portal.
 *
 * mokulab's taxonomy has an explicit top level now:
 *
 *   domain (building | landscape)
 *     └ group            a functional system in IFC terms, decided in a known phase
 *     │                  (Zones · Structure · Envelope · Interiors · Services | Planting · Hardscape · Grading)
 *     │                  IfcZone / IfcBuiltSystem / IfcDistributionSystem
 *         └ component family (CLT panels, glulam members, interior wall assemblies ...) with its IFC class
 *             └ catalog entry → IFC type + Pset_MokuLab_* + bSDD class + IDS spec + detail component
 *
 *   system profile     (mass timber, gypsum partitions | living systems): the unit the rules and
 *                      skills are written against; a material profile is a tag on groups, not a branch.
 *
 * Systems and families come from systems_hierarchy.json, which also carries
 * the rules that decide which of them a project calls for (required,
 * recommended, optional, not applicable) from its Project Setup inputs. The
 * worker resolves the same JSON; src/lib/SystemsHierarchy.ts mirrors it here.
 *
 * Capabilities (program-to-shell, component library, IDS-gated permit set,
 * fabrication package) are planned once and are domain-agnostic. A domain
 * fixes the spatial container a project is authored into and the roles in
 * the room; a system profile adds catalogs, types, and IDS tiers.
 *
 * Materials are not a domain of their own: they are the shared, DERIVED
 * vocabulary every entry references by name, so a project's material
 * take-off (and its life-cycle / carbon picture) is derived from the types
 * placed in it. The Library exposes that inversion as "used by" per
 * material; the field contract lives in materials.json `lifecycle`.
 *
 * The portal (Overview) renders the domain list from this file, a domain
 * page renders its systems, delivery phases, role cards and standards
 * cards from it, the Learn More overlay reads the same phases, and the
 * admin Library tab filters catalogs by `catalogDomain`. Copy for
 * designers lives here, not in component templates, so a change to what a
 * domain promises is one edit.
 *
 * Mirrors the Python side: scripts/ifc_worker/pipeline/bim/data/catalogs.json
 * carries the same domain and system ids for the catalogs.
 */

import designBuildPattern from '../../scripts/ifc_worker/pipeline/bim/data/design_build_pattern.json';
import communityTypes from '../../scripts/ifc_worker/pipeline/bim/data/community_building_types.json';
import systemsHierarchy from '../../scripts/ifc_worker/pipeline/bim/data/systems_hierarchy.json';

export type DomainId = 'building' | 'landscape';

export type PhaseStatus = 'available' | 'in-test' | 'planned';

export interface PhaseCard {
    id: string;
    title: string;
    /** Design-Build phase label, e.g. "Programming", "SD–DD". */
    phase: string;
    /** BIMForum LOD label, e.g. "LOD 100". */
    lod: string;
    status: PhaseStatus;
    blurb: string;
    /** Font Awesome class, e.g. "fa-list-ol". */
    icon: string;
    /** Accent color token or hex for the card's top rule. */
    accent: string;
}

export interface SystemProfile {
    id: string;
    title: string;
    status: PhaseStatus;
    blurb: string;
    /** Library catalog ids this system contributes (see catalogs.json). */
    catalogs: string[];
    /** Short, plain statement of what it becomes in IFC. */
    ifc: string;
    /** function (owns a group) or material_profile (a construction method shown as a tag, the way IFC carries it). */
    kind: 'function' | 'material_profile';
    /** Component families inside the system (one catalog, one authoring module, one detail kind each). */
    families: { id: string; title: string; group: string; catalog: string | null; status: PhaseStatus }[];
}

/**
 * System profiles for a domain, read from systems_hierarchy.json — the one
 * source the Python worker resolves applicability from. Catalogs are the
 * union of the system's families' catalogs.
 */
function systemProfiles(domain: DomainId): SystemProfile[] {
    return (systemsHierarchy.systems as any[]).filter(s => s.domain === domain).map(s => ({
        id: s.id, title: s.title, status: s.status as PhaseStatus, blurb: s.blurb, ifc: s.ifc, kind: s.kind ?? 'function',
        catalogs: [...new Set<string>((s.families as any[]).map(f => f.catalog as string | null).filter((c): c is string => !!c))],
        families: (s.families as any[]).map(f => ({ id: f.id, title: f.title, group: f.group, catalog: f.catalog ?? null, status: f.status as PhaseStatus })),
    }));
}

export interface RoleCard {
    title: string;
    blurb: string;
    icon: string;
    accent: string;
}

export interface StandardsCard {
    title: string;
    blurb: string;
    icon: string;
    accent: string;
}

export interface DomainDefinition {
    id: DomainId;
    /** Tab label. */
    navTitle: string;
    /** Card and page title. */
    title: string;
    /** One line under the title. */
    tagline: string;
    /** Two sentences on the branch card: what you design here, and why this system came first. */
    pitch: string;
    /** Lead paragraph on the domain page. */
    lead: string;
    icon: string;
    accent: string;
    /** Whether the domain is reachable in this build. */
    enabled: boolean;
    /** Spatial container the domain authors into, in plain words. */
    container: string;
    /** Actor roles the domain dispatches Psets for. */
    roles: string[];
    systems: SystemProfile[];
    phases: PhaseCard[];
    roleCards: RoleCard[];
    standards: StandardsCard[];
    /** Status-bar hint on the domain page. */
    statusHint: string;
}

/** One step of the Design-Build pattern as the portal shows it, domain-neutral. */
export interface PatternStep {
    title: string;
    blurb: string;
    icon: string;
}

/** Platform-level copy for the portal: the Systems Lab. */
export const PLATFORM = {
    /** What the landing page and the first tab are called. */
    name: 'Systems Lab',
    headline: 'A systems lab for Design-Build workflows.',
    lead:
        'Design as systems: parts that already carry their specs, placed in zones that hold still, ' +
        'checked at every phase, and handed to the people who make and grow them. Two branches ' +
        'share one pattern and one open model. Buildings, starting with mass timber, designed as ' +
        'assemblies made in a factory. Landscapes, starting with living systems, kept as a commons ' +
        'every project extends to its site.',
    /** The pattern every branch follows, in the words a designer uses: the pattern JSON's verbs, each a projection of its phases (one spine). */
    pattern: (designBuildPattern.verbs as { id: string; title: string; icon: string; blurb: string; phases: string[] }[])
        .map(v => ({ title: v.title, icon: v.icon, blurb: v.blurb })) as PatternStep[],
    trustLine:
        'Every model is open IFC 4.3. Every check is a readable IDS checklist. Every catalog entry cites its source.',
    mission: 'From program to permit to fabrication or planting, from one model',
};

/**
 * The Mokunet network message, as MokuLab restates it on the introduction
 * screen and in the Info dock. Facts and phrasing follow mokunet-org's
 * CANON.md and VOICE.md (coordination records 2026-09-23-community-access-intro
 * and 2026-10-05-lifecycle-lab-messaging): lead with place, one shared trusted
 * map, you see your moku and your kuleana, honest about stage; then the whole
 * life of a place (design, build, operate) on one governed model, buildings
 * made as assemblies, the narrowed types as example projects that each stand
 * in for a project container owned by a subscribing role, and living systems
 * as a commons. The canonical pages are linked, never re-explained.
 */
export const NETWORK = {
    name: 'Mokunet',
    eyebrow: 'Part of the Mokunet network · Mokupuni Hawaiʻi',
    headline: 'Design, build, operate.',
    lead:
        'Mokunet holds the islands of Hawaiʻi as one shared, trusted map, organized by their 33 traditional moku. ' +
        'MokuLab is where a community plans the whole life of what its place needs, designed, built, and operated ' +
        'from one open model, and hands the network that model as a governed record. ' +
        'You see your moku and your kuleana: your role and your place shape the view.',
    points: [
        { title: 'Place first', icon: 'fa-map-location-dot',
          blurb: 'Every project here is tied to its moku. Stewards define what matters for their district; Mokunet records it on one shared, trusted map. The official lines are honored, never redrawn.' },
        { title: 'One model, the whole life', icon: 'fa-arrows-spin',
          blurb: 'A project starts as a program, becomes a permit set, goes to the shop that makes its parts, and is handed to the team that runs the finished place. The zones drawn at the start are what schedules and field readings bind to; the lab models them and never holds the readings.' },
        { title: 'Made as assemblies', icon: 'fa-cubes',
          blurb: 'Buildings are designed as parts made in a factory and assembled on site: mass timber panels, beams and connections; rated walls and floors; and the production lines a food facility runs. Each assembly carries its specs, its details, and how it is made, so the shop and the permit office read the same model.' },
        { title: 'The ground as a commons', icon: 'fa-seedling',
          blurb: 'Living systems are a shared library, not one project\'s own: planting zones, plant specifications, and vegetated roofs and walls that any project extends to its site. The plant specifier stays the source, and zone and plant ids never change.' },
    ],
    /** The narrowed catalog, shown as the example projects this cycle offers: one container each. */
    examples: {
        title: 'Example projects this cycle',
        lead:
            'Each example stands in for a project of its own: a cloud project owned and run by the role that subscribes to it. ' +
            'The list is narrowed on purpose. Each type names the place it belongs to, the statute that already permits it, and the goals it serves. ' +
            'Which goals matter in a moku is set by its stewards on the platform; MokuLab reads that and never sets it.',
        columnNote: {
            building: 'One project each, owned and run by the role that subscribes to it.',
            landscape: 'A shared library every project extends to its site.',
        } as Record<DomainId, string>,
    },
    /** Canonical pages (CANON §7). In-app surfaces deep-link rather than re-explain. */
    links: [
        { title: 'Moku Stewardship', href: 'https://mokunet.org/why-moku', blurb: 'The worldview and the stewards' },
        { title: 'Community', href: 'https://mokunet.org/community', blurb: 'The hui: governance, partners, belonging' },
        { title: 'Roles', href: 'https://mokunet.org/roles', blurb: 'Your kuleana, and guided support' },
        { title: 'Open the platform', href: 'https://hawaii.mokunet.us', blurb: 'Moku Data Governance Hub' },
    ],
    site: 'https://mokunet.org',
    platform: 'https://hawaii.mokunet.us',
    proverb: { text: 'He aliʻi ka ʻāina, he kauwā ke kanaka', gloss: 'the land is chief, and the people its stewards' },
    /** Honest about stage: what this screen promises that is still being built, read from the data that carries it. */
    stage: {
        label: 'being built',
        items: [
            communityTypes.project_containers.stage_line,
            'The prefabricated assembly and food production line catalogs are being built.',
            communityTypes.landscape_commons.stage_line,
            communityTypes.moku_scoping.stage_line,
        ],
    },
    records: ['2026-09-23-community-access-intro', '2026-10-05-lifecycle-lab-messaging'],
};

/** One entry of the narrowed catalog (community_building_types.json). */
export interface CommunityType {
    id: string;
    title: string;
    examples: string[];
    place: string;
    placeTitle: string;
    statute: string[];
    statuteNote?: string;
    occupancies: string[];
    system: string;
    goals: string[];
    status: PhaseStatus;
    domain: DomainId;
}

export interface CommunityGoal { id: string; number: number; title: string; mokuReading: string; }

type RawType = { id: string; title: string; examples: string[]; place: string; statute?: string[]; statute_note?: string; occupancies?: string[]; system: string; goals: string[]; status: string };

function toCommunityType(t: RawType, domain: DomainId): CommunityType {
    const place = (communityTypes.places as Record<string, { title: string }>)[t.place];
    return {
        id: t.id, title: t.title, examples: t.examples, place: t.place, placeTitle: place?.title ?? t.place,
        statute: t.statute ?? [], statuteNote: t.statute_note, occupancies: t.occupancies ?? [], system: t.system,
        goals: t.goals, status: t.status as PhaseStatus, domain,
    };
}

/** The narrowed catalog: building types, then site types, or one domain's. */
export function listCommunityTypes(domain?: DomainId): CommunityType[] {
    const all = [
        ...(communityTypes.building_types as RawType[]).map(t => toCommunityType(t, 'building')),
        ...(communityTypes.site_types as RawType[]).map(t => toCommunityType(t, 'landscape')),
    ];
    return domain ? all.filter(t => t.domain === domain) : all;
}

/** The network's goal vocabulary (the nine SDG codes), in vocabulary order. */
export function communityGoals(): CommunityGoal[] {
    return Object.entries(communityTypes.goals as Record<string, { number: number; title: string; moku_reading: string }>)
        .map(([id, g]) => ({ id, number: g.number, title: g.title, mokuReading: g.moku_reading }));
}

export function communityGoal(id: string): CommunityGoal | undefined {
    return communityGoals().find(g => g.id === id);
}

/** Types serving any of the given goals; no goals means no scoping. Mirrors community_types.types_for_goals. */
export function communityTypesForGoals(goalIds: string[], domain?: DomainId): CommunityType[] {
    const pool = listCommunityTypes(domain);
    if (goalIds.length === 0) return pool;
    return pool.filter(t => t.goals.some(g => goalIds.includes(g)));
}

/** Human title for an actor-role id: the pattern's role title when it has one, else "Mep engineer" style. */
export function roleTitle(roleId: string): string {
    const known = (designBuildPattern.roles as Record<string, { title: string }>)[roleId];
    if (known) return known.title;
    const words = roleId.toLowerCase().split('_');
    const fixed: Record<string, string> = { mep: 'MEP', hvac: 'HVAC' };
    return words.map((w, i) => fixed[w] ?? (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
}

/** Derive phase status from the skills that serve it (mirrors pipeline.bim.design_build.phase_status). */
function patternPhaseStatus(phaseId: string): PhaseStatus {
    const order: PhaseStatus[] = ['available', 'in-test', 'planned'];
    const phase = designBuildPattern.phases.find(p => p.id === phaseId);
    if (!phase || phase.skills.length === 0) return 'planned';
    const gate = phase.exit_gate ? (designBuildPattern.gates as Record<string, { status: string }>)[phase.exit_gate.tier] : null;
    const statuses = phase.skills.map(s => (s.status === 'available' ? 'available' : 'planned') as PhaseStatus);
    if (gate && gate.status !== 'available') statuses.push((gate.status === 'in-test' ? 'in-test' : 'planned') as PhaseStatus);
    return statuses.reduce((worst, s) => (order.indexOf(s) > order.indexOf(worst) ? s : worst), 'available' as PhaseStatus);
}

interface PortalCard { id: string; domain?: string; title: string; phases: string[]; phase: string; lod: string; icon: string; accent: string; blurb: string }

/** A domain's delivery cards: projections of the pattern's phases (one spine), status derived from the phases they cover. */
function domainPhaseCards(domain: DomainId): PhaseCard[] {
    const order: PhaseStatus[] = ['available', 'in-test', 'planned'];
    return (designBuildPattern.portal_cards as PortalCard[]).filter(card => (card.domain ?? 'building') === domain).map(card => ({
        id: card.id,
        title: card.title,
        phase: card.phase,
        lod: card.lod,
        status: card.phases
            .map(patternPhaseStatus)
            .reduce((worst, s) => (order.indexOf(s) > order.indexOf(worst) ? s : worst), 'available' as PhaseStatus),
        blurb: card.blurb,
        icon: card.icon,
        accent: card.accent,
    }));
}

const BUILDING: DomainDefinition = {
    id: 'building',
    navTitle: 'Buildings',
    title: 'Building systems',
    tagline: 'Prefabricated buildings, starting with mass timber',
    pitch:
        'Design the building as parts made in a factory and assembled on site, from a room program to a permit set and machine files. ' +
        'Mass timber comes first because it is already made of parts: panels, beams, and connections.',
    lead:
        'Design your building as components made in a factory and assembled on site. Start with a ' +
        'room program, place parts that already know their specs, and get permit drawings and ' +
        'fabrication files from one model. Mass timber comes first because it is already made of ' +
        'parts: panels, beams, and connections.',
    icon: 'fa-building',
    accent: '#3b82f6',
    enabled: true,
    container: 'Project › Site › Building › Storey › Space',
    roles: [...Object.keys(designBuildPattern.roles), 'MEP_ENGINEER', 'FIRE_ENGINEER', 'MANUFACTURING_ENGINEER'],
    // Systems, their component families and the rules that decide which ones a
    // project calls for live in systems_hierarchy.json (shared with the worker).
    systems: systemProfiles('building'),
    // The delivery strip is the Design-Build pattern's portal cards. The
    // pattern JSON is the single source (agent card block is generated from
    // it too); status per card is the worst status of the phases it covers.
    phases: domainPhaseCards('building'),
    roleCards: [
        { title: 'Designers · Specify', icon: 'fa-compass-drafting', accent: '#f59e0b',
          blurb: 'Lay out rooms, then place components from the library. The specs ride along from the first sketch.' },
        { title: 'Reviewers · Check', icon: 'fa-list-check', accent: '#3b82f6',
          blurb: 'Every sheet is checked against its rules before it is issued. Every value on a sheet points back to the property it came from. See what is missing and fix it in the model, not on the drawing.' },
        { title: 'Fabricators · Build', icon: 'fa-industry', accent: '#10b981',
          blurb: 'Get shop drawings, cut lists, and machine files from the same model as the permit set. Part numbers stay stable across every revision.' },
    ],
    standards: [
        { title: 'Components carry their specs', icon: 'fa-certificate', accent: '#60a5fa',
          blurb: 'A wall you place at concept already has its layers, its fire and sound ratings, and its detail. Change the component and every drawing, schedule, and part file follows. You never redraw what the model already knows. Every catalog entry draws its own standard detail, and a project may use it only while its type still matches the entry.' },
        { title: 'Built on open standards, so nothing is locked in', icon: 'fa-layer-group', accent: '#34d399',
          blurb: 'Your model is open IFC. The rules that check each sheet are plain checklists you can read. The library covers walls, floors, and roofs today and is growing into mass timber: CLT panels, glulam beams and columns, and connections, each with a cited source.' },
    ],
    statusHint: 'Program · Component specs · Permit set · Fabrication package',
};

const LANDSCAPE: DomainDefinition = {
    id: 'landscape',
    navTitle: 'Landscape',
    title: 'Landscape systems',
    tagline: 'Living systems first: planting zones, plant specifications, and vegetated roofs and walls',
    pitch:
        'Design the ground the same way: zones with a program, plants and build-ups that carry their specs, and a planting plan the field team logs against. ' +
        'Living systems come first because a garden is already made of parts: plants, growing media, and the zones they belong to.',
    lead:
        'Design the ground the same way you design the building: as zones with a program and parts ' +
        'that carry their specs. Start from a plant specifier, place planting zones on the site, ' +
        'and get a planting plan, a maintenance schedule, and the field-data hooks your garden ' +
        'team already uses, from one model. Living systems come first because a garden is ' +
        'already a set of parts: plants, growing media, and the zones they belong to.',
    icon: 'fa-seedling',
    accent: '#22c55e',
    enabled: import.meta.env.VITE_ENABLE_LANDSCAPE === 'true',
    container: 'Project › Site › External space (planting zone)',
    roles: ['DESIGNER', 'SPECIFIER', 'LANDSCAPE_ARCHITECT', 'CIVIL_ENGINEER', 'FACILITIESMANAGER'],
    systems: systemProfiles('landscape'),
    phases: domainPhaseCards('landscape'),
    roleCards: [
        { title: 'Landscape designers · Specify', icon: 'fa-compass-drafting', accent: '#f59e0b',
          blurb: 'Keep the plant specifier as your source of truth. Every plant id and zone id stays stable, so the model and the workbook never disagree.' },
        { title: 'Reviewers · Check', icon: 'fa-list-check', accent: '#22c55e',
          blurb: 'Each plant type carries its status and source. Each zone carries its phase. The checklist tells you what is missing before the plan is issued.' },
        { title: 'Garden teams · Grow', icon: 'fa-hand-holding-droplet', accent: '#10b981',
          blurb: 'Register a planting zone as a garden plot on the field gateway and log plant health, soil moisture, and harvest against it.' },
    ],
    standards: [
        { title: 'Plants carry their specs', icon: 'fa-certificate', accent: '#4ade80',
          blurb: 'A plant you place at concept already has its botanical name, its status, and where its numbers came from. Change the specifier and the plan, the schedule, and the field labels follow.' },
        { title: 'Built on open standards, so nothing is locked in', icon: 'fa-layer-group', accent: '#34d399',
          blurb: 'Zones are external spaces under the site in open IFC. Plants use the buildingSMART vegetation Pset. The checks are readable IDS checklists. Field readings follow the same schema the garden app already uses.' },
    ],
    statusHint: 'Site program · Plant specs · Planting plan · Field and operations',
};

const ALL_DOMAINS: DomainDefinition[] = [BUILDING, LANDSCAPE];

/** Domains reachable in this build, in portal order. */
export function listDomains(): DomainDefinition[] {
    return ALL_DOMAINS.filter(d => d.enabled);
}

/** Every domain, including ones gated off, for admin surfaces. */
export function listAllDomains(): DomainDefinition[] {
    return ALL_DOMAINS.slice();
}

export function getDomain(id: string): DomainDefinition | undefined {
    return ALL_DOMAINS.find(d => d.id === id);
}

export function isDomainId(id: string): id is DomainId {
    return ALL_DOMAINS.some(d => d.id === id);
}

/** Catalog id → domain id, for the Library tab filter. Shared catalogs map to null. */
export function catalogDomain(catalogId: string): DomainId | null {
    for (const d of ALL_DOMAINS) {
        if (d.systems.some(s => s.catalogs.includes(catalogId))) return d.id;
    }
    return null;
}

export const STATUS_LABEL: Record<PhaseStatus, string> = {
    'available': 'available',
    'in-test': 'in test',
    'planned': 'planned',
};
